import React, { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle } from 'lucide-react'
import { useSubscription } from '../contexts/SubscriptionContext'

export const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { refreshSubscription } = useSubscription()
  const sessionId = searchParams.get('session_id')

  useEffect(() => {
    // Refresh subscription status when this page loads
    refreshSubscription()
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-3xl p-12 text-center">
          {/* Success Icon */}
          <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse-glow">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>

          {/* Success Message */}
          <h1 className="text-4xl font-bold text-light-primary mb-4">
            Welcome to BLP Assistant!
          </h1>

          <p className="text-xl text-light-secondary mb-8">
            Your subscription has been successfully activated. Your 7-day free trial starts now.
          </p>

          {/* Details */}
          <div className="bg-dark-tertiary/50 border border-primary-600/20 rounded-2xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-light-primary mb-4">
              What happens next?
            </h3>
            <div className="space-y-3 text-left">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary-600/20 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                  <span className="text-primary-400 text-sm font-bold">1</span>
                </div>
                <p className="text-light-secondary">
                  <span className="font-semibold text-light-primary">7 Days Free:</span> Enjoy full access to all features with no charges for 7 days
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary-600/20 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                  <span className="text-primary-400 text-sm font-bold">2</span>
                </div>
                <p className="text-light-secondary">
                  <span className="font-semibold text-light-primary">Email Confirmation:</span> Check your inbox for a receipt and welcome email
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary-600/20 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                  <span className="text-primary-400 text-sm font-bold">3</span>
                </div>
                <p className="text-light-secondary">
                  <span className="font-semibold text-light-primary">Billing Starts:</span> After your trial, you'll be charged automatically unless you cancel
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-primary-600/20 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                  <span className="text-primary-400 text-sm font-bold">4</span>
                </div>
                <p className="text-light-secondary">
                  <span className="font-semibold text-light-primary">30-Day Guarantee:</span> Not satisfied? Get a full refund within 30 days, no questions asked
                </p>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-4">
            <button
              onClick={() => navigate('/chat')}
              className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-xl font-semibold text-lg shadow-xl transition-all transform hover:scale-105"
            >
              Start Using BLP Assistant
            </button>

            <p className="text-sm text-light-tertiary">
              Session ID: {sessionId ? `...${sessionId.slice(-12)}` : 'Not available'}
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-light-tertiary text-sm">
            Need help getting started?{' '}
            <a
              href="mailto:support@listeningglass.com"
              className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
